import { SphereDivider } from "@components/common/sphere-divider";
import { CardItemDetails, CardSelector, FaroButton, FaroText } from "@faro-lotv/flat-ui";
import { useStripeProducts } from "@hooks/use-stripe-products";
import { Grid, Stack } from "@mui/material";
import { SphereDashboardAPITypes } from "@stellar/api-logic";
import {
  CREDIT_DISPLAY_NAME,
  CREDITS_DISPLAY_NAME,
} from "@src/constants/credit-constants";
import { sphereColors } from "@styles/common-colors";
import NoDataIcon from "@assets/icons/new/no_data.svg?react";
import {
  convertStripeProductToPackageCardItem,
  formatStripeDisplayPrice,
} from "@utils/stripe-product-utils";
import { EmptyPage } from "@components/common/empty-page/empty-page";
import { ReactSetStateFunction } from "@custom-types/types";
import { CreditFeatureItem } from "@pages/settings/subscription-settings/credits/credit-feature-item";
import { SphereTooltip } from "@components/common/sphere-tooltip";
import { RequestCreditQuote } from "@pages/settings/subscription-settings/credits/request-credit-quote";
import { CardSelectorSkeleton } from "@components/common/skeleton-loading/card-selector-skeleton";

/** List of credit-related features */
const CREDIT_FEATURES = [
  `Generate floorplans seamlessly using ${CREDITS_DISPLAY_NAME}.`,
  `Purchase ${CREDITS_DISPLAY_NAME} tailored to your needs.`,
  `Use ${CREDITS_DISPLAY_NAME} within 12 months before they expire.`,
];

interface Props {
  /** The currently selected credit package */
  selectedPackage?: SphereDashboardAPITypes.StripeProduct;

  /** Callback function to set the selected credit package */
  setSelectedPackage: ReactSetStateFunction<
    SphereDashboardAPITypes.StripeProduct | undefined
  >;

  /** Callback function to set shouldCheckout state */
  setShouldCheckout: ReactSetStateFunction<boolean>;
}

/** Render the credit packages list for purchasing credits. */
export function CreditPackages({
  selectedPackage,
  setSelectedPackage,
  setShouldCheckout,
}: Props): JSX.Element {
  const { stripeProducts, isLoading } = useStripeProducts();
  const cardItems = convertStripeProductToPackageCardItem(stripeProducts);

  if (cardItems.length === 0 && !isLoading) {
    return (
      <EmptyPage
        title={`No ${CREDIT_DISPLAY_NAME} packages available`}
        subtitle="Please check back later or contact support for assistance."
        icon={NoDataIcon}
      />
    );
  }

  function handleOnSelect(productId: CardItemDetails["value"]): void {
    const selectedProduct = stripeProducts.find(
      (product) => product.id === productId.toString()
    );

    if (selectedProduct) {
      setSelectedPackage(selectedProduct);
    }
  }

  return (
    <>
      <Stack
        sx={{
          alignItems: "center",
          marginTop: {
            sm: "0px",
            md: "50px",
          },
          marginBottom: "50px",
        }}
      >
        <FaroText variant="heading24"> Add {CREDITS_DISPLAY_NAME}</FaroText>
        <FaroText variant="bodyM">
          Select the {CREDIT_DISPLAY_NAME} package that best fits your needs.
        </FaroText>
      </Stack>

      <Grid container sx={{ marginX: "auto", width: { lg: "70%" } }}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6} component="div">
          <Stack sx={{ margin: "5px" }}>
            {isLoading ? (
              <CardSelectorSkeleton />
            ) : (
              <CardSelector
                cardDirection="column"
                defaultBorderColor={sphereColors.gray200}
                defaultBackgroundColor={sphereColors.pureWhite}
                selectedBackgroundColor={sphereColors.gray50}
                cardItems={cardItems}
                selectedValue={selectedPackage?.id}
                onSelect={handleOnSelect}
              />
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6} xl={6} component="div">
          <Stack
            sx={{
              backgroundColor: sphereColors.gray50,
              borderRadius: "4px",
              margin: "5px",
              padding: "32px",
            }}
          >
            <FaroText variant="heading16">Check out</FaroText>

            <SphereDivider sx={{ marginY: "12px" }} />

            {CREDIT_FEATURES.map((text, index) => (
              <CreditFeatureItem key={index} featureText={text} />
            ))}

            <SphereDivider sx={{ marginY: "12px" }} />

            <Stack
              sx={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "24px",
              }}
            >
              <FaroText variant="bodyM">Total</FaroText>
              {selectedPackage?.price.price && (
                <FaroText variant="bodyM">
                  {formatStripeDisplayPrice(selectedPackage.price.price)}
                </FaroText>
              )}
            </Stack>

            <SphereTooltip
              title="Please select a package to purchase."
              shouldShowTooltip={!selectedPackage}
            >
              <FaroButton
                fullWidth
                disabled={!selectedPackage}
                onClick={() => setShouldCheckout(true)}
              >
                Purchase {CREDITS_DISPLAY_NAME}
              </FaroButton>
            </SphereTooltip>
            <RequestCreditQuote selectedPackage={selectedPackage} />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}
